// src/components/SymptomDayModal.tsx
import React, { useEffect } from "react";
import type { SymptomDaySummary } from "../utils/symptomUtils";
import type { SymptomKey } from "../types/recovery";
import { getSymptomRisk, riskToBgColor } from "../utils/symptomUtils";
import { symptomConfigs } from "../config/symptomsConfig";

type Props = {
  day: SymptomDaySummary;
  symptomKey: SymptomKey;
  onClose: () => void;
};

export const SymptomDayModal: React.FC<Props> = ({ day, symptomKey, onClose }) => {
  useEffect(() => {
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  const parsedDate = day.dateId ? new Date(day.dateId.replace(/-/g, "/")) : null;
  const formattedDate =
    parsedDate && !isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : day.dateId ?? "Unknown";

  const config = symptomConfigs[symptomKey as keyof typeof symptomConfigs];
  const risk = getSymptomRisk(day.log ?? null, symptomKey);
  const bgColor = riskToBgColor(risk);

  // Pull the raw logged value from the log
  const symptomRecord = day.log?.symptoms as
    | Partial<Record<SymptomKey, Record<string, string>>>
    | undefined;
  const rawSymptom = symptomRecord?.[symptomKey];

  // Try common field names for the selected value
  const rawValue =
    rawSymptom?.value ??
    rawSymptom?.level ??
    rawSymptom?.pain ??
    rawSymptom?.severity ??
    "";

  // Match against config options to get the human-readable label
  const optionLabel =
    (config?.options as Array<{ value: string; label: string }>)?.find(
      (o) => o.value === rawValue
    )?.label ?? (risk === "none" ? "Not logged" : rawValue);

  const shortLabel = config?.label
    .replace(" or discomfort", "")
    .replace(" / leaks", "")
    .replace(" / sleep quality", "") ?? symptomKey;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/30 pointer-events-none" />

      <div
        className="
          pointer-events-auto relative bg-[#FCF9F7]
          rounded-t-[32px] md:rounded-[32px]
          w-full max-w-[430px]
          md:my-10 shadow-xl
          flex flex-col
          px-6 pt-[72px] pb-6
          gap-4
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          className="absolute top-6 right-6 w-6 h-6 flex items-center justify-center"
          onClick={onClose}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 13L13 1M1 1L13 13"
              stroke="#303030"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Date */}
        <h2 className="absolute top-9 left-6 font-dmSans font-semibold text-[24px] leading-[31px] text-[#0C2624]">
          {formattedDate}
        </h2>

        {/* Symptom name */}
        <div className="w-full bg-[#F5F5F5] rounded-[16px] p-4 flex items-center gap-4 h-[96px] shrink-0">
          <div
            className="w-[50px] h-[50px] rounded-full shrink-0"
            style={{ backgroundColor: bgColor }}
          />
          <div className="flex flex-col justify-center">
            <span className="font-dmSans font-semibold text-[16px] text-[#0C2624]">
              {shortLabel}
            </span>
          </div>
        </div>

        {/* Logged value */}
        <div className="w-full bg-[#F5F5F5] rounded-[16px] p-4 flex flex-col justify-center gap-2 h-[96px] shrink-0">
          <span className="font-dmSans font-semibold text-[16px] text-[#0C2624]">
            What you logged
          </span>
          <span className="font-dmSans font-semibold text-[20px] text-[#303030]">
            {optionLabel}
          </span>
        </div>
      </div>
    </div>
  );
};